import {
  elementOf,
  motionStyle,
  movementPath,
  type Board,
  type Element,
  type GameState,
  type MotionStyle,
  type Move,
  type Piece,
  type Roster,
} from '@archon3d/core';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import {
  boardModelFile,
  type CharacterInstance,
  type CharacterModelCache,
} from './characterModels';
import { BOARD_CENTER, ELEMENT_COLORS, TILE_SIZE } from './constants';
import { cellToWorld, challengeStances } from './layout';
import { PALETTES, factionColor, type Palette } from './palette';

/**
 * Pieces on the board. Characters with a `model` in the roster load their GLB (a simple shape
 * stands in until it arrives); the others render as placeholder silhouettes. A moved piece
 * travels to its square the way its character moves – walking square by square, flying in an
 * arc, drifting above the board, or blinking out and in – and holds the game until it arrives.
 * A piece that was not there before (revived, or an elemental summoned to fight) grows out of
 * nothing on its square; one that has gone shrinks away.
 */
type Shape = 'pawn' | 'scout' | 'heavy' | 'runner' | 'flyer' | 'mage';

const SHAPES: Record<string, Shape> = {
  knight: 'pawn',
  goblin: 'pawn',
  archer: 'scout',
  manticore: 'scout',
  valkyrie: 'scout',
  banshee: 'scout',
  golem: 'heavy',
  troll: 'heavy',
  unicorn: 'runner',
  basilisk: 'runner',
  djinni: 'flyer',
  shapeshifter: 'flyer',
  phoenix: 'flyer',
  dragon: 'flyer',
  wizard: 'mage',
  sorceress: 'mage',
  'elemental-earth': 'heavy',
  'elemental-water': 'runner',
  'elemental-air': 'flyer',
  'elemental-fire': 'scout',
};

/** Seconds per square for each way of moving. */
const PACE: Record<MotionStyle, number> = {
  walk: 0.3,
  jump: 0.36,
  fly: 0.24,
  float: 0.34,
  blink: 0,
};
/** Height of a hop, in tiles: high enough to read as a leap, low enough to land on the square. */
const JUMP_HEIGHT = 0.45;
const BLINK_SECONDS = 0.22;
const TURN_SECONDS = 0.18;
/** Cruise heights above the squares, in tiles. */
const FLY_HEIGHT = 0.5;
const FLOAT_HEIGHT = 0.12;
/** Run clips are filmed at a jog; the pieces hurry. */
const RUN_CLIP_SPEED = 1.8;
/**
 * The dragon's wing-beat is 1.5 s and a square takes 0.24 s, so at its authored rate a whole
 * move shows about two thirds of one beat — the wings start down and stop. Played this much
 * faster a four-square flight reads as continuous flapping, and the clip now runs unbroken for
 * the whole journey rather than restarting at every square.
 */
const FLY_CLIP_SPEED = 2.5;
/** Wing-beats per square for a flier with no clips, so the body rises and falls as wings would. */
const BEATS_PER_TILE = 1.4;
/**
 * A piece taken (x-aaa-gap#8): it stands a moment as the blow lands, topples over backwards, away
 * from its foe and gathering speed, then sinks into its square as it shrinks, with a puff of its
 * side's colour where it falls (`onFall`). It used to spin like a top as it shrank. A summoned
 * creature, whose leaving is its element's own puff, only sinks away.
 */
const VANISH = { wait: 0.2, topple: 0.45, sink: 0.45 };
/**
 * The winner of a duel settles onto the square it just took. It is already most of the way there
 * — it stepped up to challenge before the fight — so this is one short glide onto the centre,
 * and it swells and shrinks back over the same second, which is the board's way of cheering.
 */
const VICTORY_SECONDS = 1;
const VICTORY_SWELL = 0.3;
/** How long the piece already on a square takes to make room for the one coming to fight it. */
const GIVE_GROUND_SECONDS = 0.45;

/**
 * A short eased slide within one square, rather than a journey between squares: `ground` is the
 * participants making room for a challenger, `victory` the survivor taking the middle afterwards.
 */
type Settle = 'ground' | 'victory' | null;

type Phase = 'idle' | 'turning' | 'moving' | 'returning' | 'blink-out' | 'blink-in';

interface Journey {
  style: MotionStyle;
  /** Squares still ahead, as world positions; the last one may be a partial step. */
  waypoints: Vector3[];
  from: Vector3;
  to: Vector3;
  t: number;
  seconds: number;
  /** Segments done and in total, for the flight arc. */
  index: number;
  total: number;
  /** Metres between footfalls (or wing beats); 0 for a blink. */
  stride: number;
  /** Repositioning within one square rather than travelling between them; see `Settle`. */
  settle: Settle;
  /** Metres travelled since the last footfall, and footfalls so far. */
  travelled: number;
  steps: number;
  release: () => void;
}

interface Visual {
  /** Moves and turns; the body (placeholder or model) hangs under it. */
  root: TransformNode;
  id: number;
  faction: string;
  character: string;
  /** Yaw at rest: facing the enemy side. */
  restYaw: number;
  /** Where the piece rests (or heads for); not always its square while a battle is pending. */
  home: Vector3;
  placeholder: Mesh | null;
  model: CharacterInstance | null;
  imprisoned: boolean;
  /** Static models (no idle clip) get a procedural breathe/hover instead. */
  breathes: boolean;
  journey: Journey | null;
  phase: Phase;
  phaseT: number;
  yawFrom: number;
  yawTo: number;
  /** Seconds since the piece was taken, while it leaves the board. */
  vanishing: number | null;
  /** Where it stood when taken, for it to sink from; and whether it has fallen yet. */
  vanishY: number;
  fell: boolean;
}

/** A taken piece lands as it topples over. */
export interface PieceFall {
  pieceId: number;
  faction: string;
  /** Its side's colour, as the board shows it now. */
  color: string;
  /** Where its body lands, in the board's own space (that of `cellToWorld`). */
  at: Vector3;
}

export interface PieceSyncContext {
  state: GameState;
  /** The state before this one, to path the pieces that moved. */
  previous: GameState | null;
  lastMove: Move | null;
  /** Pauses the game until the returned function is called. */
  hold: () => () => void;
}

/** A moment of a piece's journey, for sound: it sets off, takes a step (or beats a wing), settles. */
export interface PieceMotionEvent {
  /** `arrive` comes as the piece settles or, for a blink, as it reappears. */
  kind: 'depart' | 'step' | 'arrive';
  pieceId: number;
  character: string;
  style: MotionStyle;
  /** World position of the piece's feet. */
  at: { x: number; y: number; z: number };
  /** Steps taken so far in this journey, to alternate feet. */
  step: number;
}

export interface PieceLayer {
  /** Creates, moves and removes pieces so they match the pieces in `state`. */
  sync(ctx: PieceSyncContext): void;
  pieceIdOf(mesh: AbstractMesh | null | undefined): number | undefined;
  /** Reports departures, footfalls and arrivals of travelling pieces; returns the unsubscribe. */
  onMotion(listener: (event: PieceMotionEvent) => void): () => void;
  /** Reports each taken piece as it lands, toppled over; returns the unsubscribe. */
  onFall(listener: (fall: PieceFall) => void): () => void;
  /** World position of a piece's feet, while it is on the board or still vanishing. */
  positionOf(pieceId: number): { x: number; y: number; z: number } | undefined;
  /** How far a piece's top stands over its feet, in metres: what a sign over it must clear. */
  topOf(pieceId: number): number | undefined;
  /** Advances journeys and vanish animations; call every frame. */
  update(dtSeconds: number): void;
  /** Recolours the placeholder pieces in a palette's faction colours; models keep their textures. */
  setPalette(palette: Palette): void;
  dispose(): void;
}

export function createPieceLayer(
  scene: Scene,
  board: Board,
  roster: Roster,
  models: CharacterModelCache,
  onMesh: (mesh: AbstractMesh) => void,
  parent: TransformNode | null = null,
): PieceLayer {
  const materials = new Map<string, { normal: StandardMaterial; imprisoned: StandardMaterial }>();
  const facing = new Map<string, number>();
  /** The colours the board shows now, for a taken piece's puff. */
  let palette: Palette = PALETTES.classic;
  const paint = (next: Palette) => {
    palette = next;
    for (const faction of board.def.factions) {
      const mats = materials.get(faction.id);
      if (!mats) continue;
      mats.normal.diffuseColor = Color3.FromHexString(factionColor(palette, faction));
      mats.imprisoned.diffuseColor = mats.normal.diffuseColor.scale(0.45);
    }
  };
  for (const faction of board.def.factions) {
    const normal = new StandardMaterial(`piece-${faction.id}`, scene);
    normal.specularColor = new Color3(0.5, 0.5, 0.5);
    normal.specularPower = 48;
    const imprisoned = normal.clone(`piece-${faction.id}-imprisoned`);
    imprisoned.emissiveColor = new Color3(0.25, 0.05, 0.35);
    materials.set(faction.id, { normal, imprisoned });

    // Pieces face away from their home edge, towards the middle of the board.
    const home = board.def.placements.filter((p) => p.faction === faction.id);
    if (home.length > 0) {
      const cx = home.reduce((s, p) => s + p.x, 0) / home.length;
      const cy = home.reduce((s, p) => s + p.y, 0) / home.length;
      const at = cellToWorld(board, cx, cy);
      facing.set(faction.id, yawTowards(at, BOARD_CENTER));
    }
  }
  paint(PALETTES.classic);
  // Summoned elementals wear their element rather than their side, so the board shows what came.
  const elementMaterials = new Map<Element, StandardMaterial>();
  const elementMaterial = (element: Element): StandardMaterial => {
    let material = elementMaterials.get(element);
    if (!material) {
      material = new StandardMaterial(`piece-elemental-${element}`, scene);
      const color = Color3.FromHexString(ELEMENT_COLORS[element]);
      material.diffuseColor = color;
      material.emissiveColor = color.scale(0.4);
      material.specularColor = new Color3(0.6, 0.6, 0.6);
      material.specularPower = 32;
      elementMaterials.set(element, material);
    }
    return material;
  };
  const visuals = new Map<number, Visual>();
  const vanishing: Visual[] = [];
  let disposed = false;
  let clock = 0;

  const tag = (mesh: AbstractMesh, pieceId: number) => {
    mesh.metadata = { ...(mesh.metadata as object | null), pieceId };
    mesh.isPickable = true;
    mesh.receiveShadows = true;
    onMesh(mesh);
  };

  const body = (v: Visual): TransformNode | null => v.model?.root ?? v.placeholder;

  /**
   * Each body's height over its feet at rest size, measured once: pieces differ by a third (a
   * 0.085 m Knight, a 0.11 m Troll), and a staff or a crest can stand above the head. The
   * placeholder is measured first and the model when it replaces it. The root grows in, swells at
   * a victory and stretches as it vanishes, so the height given is the rest one at its scale now.
   */
  const tops = new WeakMap<TransformNode, number>();
  const topOf = (v: Visual): number | undefined => {
    const b = body(v);
    const scale = v.root.scaling.y;
    if (!b) return undefined;
    let rest = tops.get(b);
    if (rest === undefined) {
      if (scale < 0.2) return undefined;
      const { max } = b.getHierarchyBoundingVectors(true);
      rest = (max.y - v.root.getAbsolutePosition().y) / scale;
      if (!Number.isFinite(rest) || rest <= 0) return undefined;
      tops.set(b, rest);
    }
    return rest * scale;
  };

  const listeners = new Set<(event: PieceMotionEvent) => void>();
  const fallListeners = new Set<(fall: PieceFall) => void>();
  /** A taken piece has toppled over: its body lies behind where it stood, facing its foe. */
  const fell = (v: Visual) => {
    const yaw = v.root.rotation.y;
    const faction = board.def.factions.find((f) => f.id === v.faction);
    const fall: PieceFall = {
      pieceId: v.id,
      faction: v.faction,
      color: faction ? factionColor(palette, faction) : '#ffffff',
      at: v.root.position.add(
        new Vector3(-Math.sin(yaw), 0, -Math.cos(yaw)).scale(TILE_SIZE * 0.3),
      ),
    };
    for (const listener of fallListeners) listener(fall);
  };
  const worldPosition = (v: Visual) => {
    const p = v.root.getAbsolutePosition();
    return { x: p.x, y: p.y, z: p.z };
  };
  const emit = (kind: PieceMotionEvent['kind'], v: Visual, journey: Journey) => {
    if (listeners.size === 0) return;
    const event: PieceMotionEvent = {
      kind,
      pieceId: v.id,
      character: v.character,
      style: journey.style,
      at: worldPosition(v),
      step: journey.steps,
    };
    for (const listener of listeners) listener(event);
  };

  const spawn = (piece: Piece): Visual => {
    const root = new TransformNode(`piece-${piece.id}`, scene);
    root.parent = parent;
    root.position.copyFrom(cellToWorld(board, piece.x, piece.y));
    const restYaw = facing.get(piece.faction) ?? 0;
    root.rotation.y = restYaw;
    const placeholder = createPlaceholder(scene, SHAPES[piece.character] ?? 'pawn', piece);
    placeholder.parent = root;
    tag(placeholder, piece.id);
    const visual: Visual = {
      root,
      id: piece.id,
      faction: piece.faction,
      character: piece.character,
      restYaw,
      home: root.position.clone(),
      placeholder,
      model: null,
      imprisoned: piece.imprisoned,
      breathes: false,
      journey: null,
      phase: 'idle',
      phaseT: 0,
      yawFrom: restYaw,
      yawTo: restYaw,
      vanishing: null,
      vanishY: 0,
      fell: false,
    };
    const model = roster[piece.character]?.model;
    if (model) {
      void models
        .instantiate(model, `piece-${piece.id}-model`, { file: boardModelFile(model) })
        .then((instance) => {
          // The duel shows the full model: fetched now, it is ready when this piece fights.
          if (model.boardFile) models.prefetch(model.file);
          if (disposed || visual.vanishing !== null || visuals.get(piece.id) !== visual) {
            instance.dispose();
            return;
          }
          instance.root.parent = root;
          for (const mesh of instance.meshes) tag(mesh, piece.id);
          visual.placeholder?.dispose();
          visual.placeholder = null;
          visual.model = instance;
          visual.breathes = instance.play('idle') === undefined;
          if (visual.journey && visual.phase === 'moving') playTravelClip(visual);
        })
        .catch((err: unknown) => console.warn(`model for ${piece.character} failed to load`, err));
    }
    return visual;
  };

  const applyMaterial = (v: Visual, piece: Piece) => {
    const element = roster[piece.character]?.summoned ? elementOf(piece.character) : undefined;
    const mats = materials.get(piece.faction);
    if (v.placeholder) {
      if (element) v.placeholder.material = elementMaterial(element);
      else if (mats) v.placeholder.material = piece.imprisoned ? mats.imprisoned : mats.normal;
    }
    v.imprisoned = piece.imprisoned;
  };

  /** A piece that was not there before grows out of nothing where it stands (the second half of a blink). */
  const appear = (v: Visual, at: Vector3, hold: () => () => void) => {
    v.root.position.copyFrom(at);
    v.home = at.clone();
    v.root.scaling.setAll(0);
    // Reported below before the first render: the world position must be current.
    v.root.computeWorldMatrix(true);
    const journey: Journey = {
      style: 'blink',
      waypoints: [],
      from: at.clone(),
      to: at.clone(),
      t: 1,
      seconds: 0,
      index: 0,
      total: 1,
      stride: 0,
      settle: null,
      travelled: 0,
      steps: 0,
      release: hold(),
    };
    v.journey = journey;
    v.phaseT = 0;
    v.phase = 'blink-in';
    // Reported as an arrival, like a blink reappearing: the same shimmer greets it.
    emit('arrive', v, journey);
  };

  /** Starts a journey along waypoints; the last waypoint is where the piece will rest. */
  const travel = (
    v: Visual,
    style: MotionStyle,
    waypoints: Vector3[],
    hold: () => () => void,
    settle: Settle = null,
  ) => {
    if (waypoints.length === 0) return;
    v.journey?.release();
    v.home = waypoints[waypoints.length - 1]!.clone();
    const journey: Journey = {
      style,
      waypoints: waypoints.slice(),
      from: v.root.position.clone(),
      to: v.root.position.clone(),
      t: 1,
      seconds: 0,
      index: 0,
      total: waypoints.length,
      // A glide takes no steps: no footfalls to hear and no bob to keep time with.
      stride: settle !== null ? 0 : strideFor(style, roster[v.character]?.tier ?? 1),
      settle,
      travelled: 0,
      steps: 0,
      release: hold(),
    };
    v.journey = journey;
    v.phaseT = 0;
    emit('depart', v, journey);
    if (style === 'blink') {
      v.phase = 'blink-out';
      return;
    }
    nextSegment(v, journey);
    v.yawFrom = v.root.rotation.y;
    if (settle !== null) {
      // No pivot and no walk cycle: it is standing on the square already, so it drifts across it
      // while turning back to face the enemy side.
      v.yawTo = v.restYaw;
      v.phase = 'moving';
      return;
    }
    v.yawTo = style === 'float' ? v.restYaw : yawTowards(journey.from, journey.to);
    v.phase = 'turning';
  };

  const nextSegment = (v: Visual, journey: Journey): boolean => {
    let to = journey.waypoints.shift();
    // A muster arrival can change the stance while this piece is on its final approach. Finish
    // that segment before sliding to the new stance, keeping the same journey and session hold.
    if (!to && !journey.to.equalsWithEpsilon(v.home, 1e-4)) {
      to = v.home.clone();
      journey.settle = 'ground';
      journey.stride = 0;
      journey.index = 0;
      journey.total = 1;
      v.model?.play('idle');
    }
    if (!to) return false;
    journey.from = v.root.position.clone();
    journey.from.y = cellToWorld(board, 0, 0).y;
    journey.to = to;
    journey.t = 0;
    journey.seconds =
      journey.settle === 'victory'
        ? VICTORY_SECONDS
        : journey.settle === 'ground'
          ? GIVE_GROUND_SECONDS
          : (PACE[journey.style] * Vector3.Distance(journey.from, journey.to)) / TILE_SIZE;
    if (journey.settle !== null) {
      v.yawFrom = v.root.rotation.y;
      v.yawTo = v.restYaw;
    } else if (journey.style !== 'float') {
      v.yawFrom = v.root.rotation.y;
      v.yawTo = yawTowards(journey.from, journey.to);
    }
    return true;
  };

  const playTravelClip = (v: Visual) => {
    const journey = v.journey;
    if (!journey || !v.model) return;
    if (journey.style === 'jump') {
      // A real leap when the model has one; the arc alone carries a model without.
      if (!v.model.play('jump') && !v.model.play('run')) v.model.play('walk');
    } else if (journey.style === 'walk') {
      const run = v.model.play('run') ?? v.model.play('walk');
      if (run) run.speedRatio = RUN_CLIP_SPEED;
    } else if (journey.style === 'fly' || journey.style === 'float') {
      const fly = v.model.play('fly');
      if (fly) fly.speedRatio = FLY_CLIP_SPEED;
      else v.model.play('idle');
    }
  };

  const arrive = (v: Visual) => {
    const journey = v.journey;
    if (!journey) return;
    v.root.position.copyFrom(v.home);
    v.root.scaling.setAll(1);
    const b = body(v);
    if (b) {
      b.position.set(0, 0, 0);
      b.rotation.set(0, v.model ? b.rotation.y : 0, 0);
    }
    if (v.model) {
      const idle = v.model.play('idle');
      if (idle) idle.speedRatio = 1;
    }
    v.journey = null;
    v.phase = 'idle';
    // A blink reports its arrival as it reappears, before it has grown back to size.
    if (journey.style !== 'blink') emit('arrive', v, journey);
    journey.release();
  };

  return {
    sync({ state, previous, lastMove, hold }) {
      const seen = new Set<number>();
      const battle =
        state.phase === 'battle' || state.phase === 'muster' ? state.pendingBattle : null;
      const previousBattle =
        previous?.phase === 'battle' || previous?.phase === 'muster'
          ? previous.pendingBattle
          : null;
      const alreadyGathered = new Set(
        battle &&
          previousBattle &&
          previous?.turn === state.turn &&
          battle.attackerId === previousBattle.attackerId &&
          battle.defenderId === previousBattle.defenderId &&
          battle.x === previousBattle.x &&
          battle.y === previousBattle.y
          ? (previousBattle.participantIds ?? [
              previousBattle.attackerId,
              previousBattle.defenderId,
            ])
          : [],
      );
      // A pending fight is worked out once, ahead of the pieces: where the two of them stand on
      // the square depends on the line the attacker comes in on, and the defender cannot know
      // which way to give ground without it.
      let challengePath: { x: number; y: number }[] = [];
      const participantIds = battle
        ? (battle.participantIds ?? [battle.attackerId, battle.defenderId])
        : [];
      const stances = new Map<number, Vector3>();
      if (battle) {
        const attacker = state.pieces.find((p) => p.id === battle.attackerId);
        const was = previous?.pieces.find((p) => p.id === battle.attackerId);
        if (attacker) {
          if (was && previous)
            challengePath = movementPath({ board, roster }, previous, was, battle.x, battle.y);
          const approach = challengePath[challengePath.length - 2] ?? {
            x: attacker.x,
            y: attacker.y,
          };
          const pair = challengeStances(board, approach, { x: battle.x, y: battle.y });
          stances.set(battle.attackerId, pair.attacker);
          stances.set(battle.defenderId, pair.defender);
        }
        const centre = cellToWorld(board, battle.x, battle.y);
        participantIds.forEach((id, index) => {
          if (stances.has(id)) return;
          const angle = (index / participantIds.length) * Math.PI * 2 - Math.PI / 2;
          stances.set(
            id,
            centre.add(new Vector3(Math.cos(angle) * 0.035, 0, Math.sin(angle) * 0.035)),
          );
        });
      }
      // The fight that has just been settled, which tells a survivor claiming its square apart
      // from a piece that has simply moved.
      const settled = previous?.phase === 'battle' ? previous.pendingBattle : null;

      for (const piece of state.pieces) {
        seen.add(piece.id);
        const before = previous?.pieces.find((p) => p.id === piece.id);
        const challenges = !!battle && participantIds.includes(piece.id);
        const defends = battle?.defenderId === piece.id;
        // Where the piece is headed: an attacker with a fight pending is headed for the square it
        // is contesting, not the one the rules still have it standing on.
        const goal = challenges ? { x: battle.x, y: battle.y } : { x: piece.x, y: piece.y };
        // Pieces that have not moved path in one comparison, so most of the board costs nothing.
        const path = challenges
          ? before && previous
            ? movementPath({ board, roster }, previous, before, goal.x, goal.y)
            : challengePath
          : before && previous
            ? movementPath({ board, roster }, previous, before, goal.x, goal.y)
            : [];
        const target = cellToWorld(board, goal.x, goal.y);
        const stance = stances.get(piece.id);
        if (stance && (challenges || defends)) target.copyFrom(stance);
        // The fight is over and this piece is the one still standing. Either way it is already on
        // the square, off to one side of the middle where it fought: it has the middle to take and
        // nothing to walk. Pathing it afresh from the square the attacker set out from is what
        // used to make a winner march its whole move a second time.
        const won =
          settled !== null &&
          (settled.participantIds ?? [settled.attackerId, settled.defenderId]).includes(piece.id) &&
          piece.x === settled.x &&
          piece.y === settled.y;
        let v = visuals.get(piece.id);
        if (!v) {
          v = spawn(piece);
          visuals.set(piece.id, v);
          // The first sync sets the board up; later newcomers are conjured and grow into view.
          if (previous) appear(v, target, hold);
        }
        if (!target.equalsWithEpsilon(v.home, 1e-4)) {
          const repositions = challenges && alreadyGathered.has(piece.id);
          if (repositions && v.journey) {
            // The rules retain a joiner's original cell until combat resolves. Re-pathing from
            // it would replay the approach. Retarget only the unfinished journey instead.
            const journey = v.journey;
            v.home = target.clone();
            if (journey.waypoints.length > 0)
              journey.waypoints[journey.waypoints.length - 1] = target;
            if (v.phase === 'returning' && nextSegment(v, journey)) v.phase = 'moving';
            if (v.phase === 'blink-in') v.root.position.copyFrom(target);
            applyMaterial(v, piece);
            continue;
          }
          const character = roster[piece.character];
          const teleported =
            lastMove?.type === 'spell' &&
            (lastMove.spell === 'teleport' || lastMove.spell === 'exchange');
          const style: MotionStyle =
            !character || teleported || !before ? 'blink' : motionStyle(character);
          const settle: Settle =
            style === 'blink' ? null : won ? 'victory' : defends || repositions ? 'ground' : null;
          let waypoints: Vector3[];
          if (settle !== null || style === 'blink' || path.length === 0) waypoints = [target];
          else {
            waypoints = path.map((c) => cellToWorld(board, c.x, c.y));
            if (challenges) waypoints[waypoints.length - 1] = target;
          }
          travel(v, style, waypoints, hold, settle);
        }
        applyMaterial(v, piece);
      }
      for (const [id, v] of visuals) {
        if (seen.has(id)) continue;
        visuals.delete(id);
        if (v.journey) {
          // Taken mid-journey: whoever waits for the arrival (a sound) must not wait for ever.
          emit('arrive', v, v.journey);
          v.journey.release();
        }
        v.journey = null;
        v.vanishing = 0;
        v.vanishY = v.root.position.y;
        vanishing.push(v);
      }
    },
    pieceIdOf(mesh) {
      const id = (mesh?.metadata as { pieceId?: number } | undefined)?.pieceId;
      return typeof id === 'number' ? id : undefined;
    },
    onMotion(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    onFall(listener) {
      fallListeners.add(listener);
      return () => fallListeners.delete(listener);
    },
    positionOf(pieceId) {
      const v = visuals.get(pieceId) ?? vanishing.find((x) => x.id === pieceId);
      return v ? worldPosition(v) : undefined;
    },
    topOf(pieceId) {
      const v = visuals.get(pieceId) ?? vanishing.find((x) => x.id === pieceId);
      return v ? topOf(v) : undefined;
    },
    update(dt) {
      clock += dt;
      for (const v of visuals.values()) {
        const journey = v.journey;
        if (!journey) {
          if (v.breathes && v.model) {
            const phase = clock * 1.6 + v.root.uniqueId;
            v.model.root.scaling.y = (v.model.root.scaling.x || 1) * (1 + 0.012 * Math.sin(phase));
            v.model.root.position.y = 0.0008 * (1 + Math.sin(phase * 0.5));
          }
          continue;
        }
        const b = body(v);
        switch (v.phase) {
          case 'turning':
          case 'returning': {
            v.phaseT = Math.min(1, v.phaseT + dt / TURN_SECONDS);
            v.root.rotation.y = lerpYaw(v.yawFrom, v.yawTo, easeInOut(v.phaseT));
            if (v.phaseT < 1) break;
            if (v.phase === 'turning') {
              v.phase = 'moving';
              // The turn is over, so the ground the corner-rounding below turns *from* is where
              // the piece is now. Without this it snaps back to the pre-turn facing on the first
              // frame of travel.
              v.yawFrom = v.root.rotation.y;
              // The clip starts with the motion rather than during the pivot: a piece running on
              // the spot while it turns is the same double-motion in miniature.
              playTravelClip(v);
            } else arrive(v);
            break;
          }
          case 'moving': {
            const before = journey.t;
            journey.t = Math.min(1, journey.t + dt / Math.max(0.01, journey.seconds));
            const overall = (journey.index + journey.t) / journey.total;
            // A march holds its pace between squares; a slide within one eases off at both ends.
            Vector3.LerpToRef(
              journey.from,
              journey.to,
              journey.settle !== null ? easeInOut(journey.t) : journey.t,
              v.root.position,
            );
            if (journey.settle === 'victory')
              v.root.scaling.setAll(1 + VICTORY_SWELL * Math.sin(overall * Math.PI));
            if (journey.stride > 0) {
              journey.travelled +=
                Vector3.Distance(journey.from, journey.to) * (journey.t - before);
              while (journey.travelled >= journey.stride) {
                journey.travelled -= journey.stride;
                journey.steps += 1;
                emit('step', v, journey);
              }
            }
            if (journey.style !== 'float') {
              // Corners are rounded off by turning a little into each new segment.
              v.root.rotation.y = lerpYaw(v.yawFrom, v.yawTo, Math.min(1, journey.t * 4));
            }
            // Everything procedural rises and settles over the journey, because `arrive()` clears
            // the lot in one frame: a hover or a lean that was applied whole is a pop when it is
            // removed whole.
            const settle = Math.min(1, overall * 6) * Math.min(1, (1 - overall) * 6);
            if (journey.settle !== null) {
              // Nothing else: no wing beat, no hop, no footfall bob under a piece that is only
              // shifting its feet on a square it is already standing on.
            } else if (journey.style === 'fly') {
              v.root.position.y += Math.sin(overall * Math.PI) * FLY_HEIGHT * TILE_SIZE;
              if (b && !v.model?.animations.size) {
                // The phoenix and the valkyrie are static meshes with no skeleton, so no wing can
                // move on its own. The whole body is flown instead: it lifts and dips on a beat,
                // and pitches nose-down as it rises, the way a wing-stroke would carry it. Driven
                // by the ground covered rather than the clock, so every flight beats the same.
                const beat = journey.total * BEATS_PER_TILE * overall * 2 * Math.PI;
                b.position.y = settle * 0.05 * TILE_SIZE * Math.sin(beat);
                b.rotation.x = 0.25 * Math.sin(overall * Math.PI) - settle * 0.06 * Math.cos(beat);
                // A gentle bank towards the way it is turning, level again on arrival.
                b.rotation.z = settle * 0.12 * Math.sin(beat * 0.5);
              }
            } else if (journey.style === 'float') {
              v.root.position.y +=
                settle * FLOAT_HEIGHT * TILE_SIZE * (1 + 0.25 * Math.sin(clock * 5));
              if (b) b.rotation.z = settle * 0.06 * Math.sin(clock * 3);
            } else if (journey.style === 'jump') {
              // One hop per square: up and over, leaning into the leap, upright on landing.
              const hop = Math.sin(journey.t * Math.PI);
              v.root.position.y += hop * JUMP_HEIGHT * TILE_SIZE;
              if (b) b.rotation.x = 0.22 * hop;
            } else if (b && !v.model?.animations.size) {
              // Driven by the ground covered rather than the clock, so the bob keeps step with
              // the footfalls the journey emits instead of drifting against them.
              const pace = journey.stride > 0 ? journey.travelled / journey.stride : 0;
              b.position.y = settle * Math.abs(Math.sin(pace * Math.PI)) * 0.06 * TILE_SIZE;
              b.rotation.x = settle * 0.1;
            }
            if (journey.t < 1) break;
            journey.index += 1;
            if (nextSegment(v, journey)) break;
            v.phaseT = 0;
            v.yawFrom = v.root.rotation.y;
            v.yawTo = v.restYaw;
            v.phase = 'returning';
            break;
          }
          case 'blink-out': {
            v.phaseT = Math.min(1, v.phaseT + dt / BLINK_SECONDS);
            const k = 1 - easeInOut(v.phaseT);
            v.root.scaling.set(k, 1 + (1 - k) * 0.6, k);
            if (v.phaseT < 1) break;
            v.root.position.copyFrom(v.home);
            v.phaseT = 0;
            v.phase = 'blink-in';
            emit('arrive', v, journey);
            break;
          }
          case 'blink-in': {
            v.phaseT = Math.min(1, v.phaseT + dt / BLINK_SECONDS);
            const k = easeInOut(v.phaseT);
            v.root.scaling.set(k, 1 + (1 - k) * 0.6, k);
            if (v.phaseT >= 1) arrive(v);
            break;
          }
          case 'idle':
            break;
        }
      }
      for (let i = vanishing.length - 1; i >= 0; i--) {
        const v = vanishing[i]!;
        const t = (v.vanishing = (v.vanishing ?? 0) + dt);
        const summoned = !!roster[v.character]?.summoned;
        const fallsAt = summoned ? 0 : VANISH.wait + VANISH.topple;
        if (!summoned) {
          const over = Math.min(1, Math.max(0, (t - VANISH.wait) / VANISH.topple));
          v.root.rotation.x = -(Math.PI / 2) * over * over;
        }
        if (t >= fallsAt && !v.fell) {
          v.fell = true;
          if (!summoned) fell(v);
        }
        const sunk = Math.min(1, Math.max(0, (t - fallsAt) / VANISH.sink));
        const k = 1 - sunk;
        v.root.scaling.set(k, k, k);
        v.root.position.y = v.vanishY - sunk * TILE_SIZE * 0.3;
        if (k <= 0) {
          disposeVisual(v);
          vanishing.splice(i, 1);
        }
      }
    },
    setPalette: paint,
    dispose() {
      disposed = true;
      for (const v of visuals.values()) {
        v.journey?.release();
        disposeVisual(v);
      }
      for (const v of vanishing) disposeVisual(v);
      for (const material of elementMaterials.values()) material.dispose();
      elementMaterials.clear();
      visuals.clear();
      vanishing.length = 0;
      listeners.clear();
      fallListeners.clear();
    },
  };
}

/** Babylon's rotation.y that points a model's +Z forward from one spot to another. */
function yawTowards(from: Vector3, to: Vector3): number {
  return Math.atan2(to.x - from.x, to.z - from.z);
}

function lerpYaw(a: number, b: number, t: number): number {
  let d = b - a;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return a + d * t;
}

function disposeVisual(v: Visual): void {
  v.model?.dispose();
  v.placeholder?.dispose();
  v.root.dispose();
}

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/**
 * Metres between footfalls: a pawn takes two steps per square, a leader little more than one;
 * wings beat a little slower than feet fall. A floating piece reports "steps" only so its hum
 * can follow it.
 */
function strideFor(style: MotionStyle, tier: number): number {
  switch (style) {
    case 'walk':
      return TILE_SIZE * (0.42 + 0.09 * tier);
    case 'jump':
      // One landing per square.
      return TILE_SIZE;
    case 'fly':
      return TILE_SIZE * 0.55;
    case 'float':
      return TILE_SIZE * 0.5;
    case 'blink':
      return 0;
  }
}

function createPlaceholder(scene: Scene, shape: Shape, piece: Piece): Mesh {
  const s = TILE_SIZE;
  const base = MeshBuilder.CreateCylinder(
    'base',
    { height: s * 0.1, diameter: s * 0.62, tessellation: 20 },
    scene,
  );
  base.position.y = s * 0.05;
  const body = createBody(scene, shape, s);
  const mesh = Mesh.MergeMeshes([base, body], true, true, undefined, false, false)!;
  mesh.name = `placeholder-${piece.id}`;
  return mesh;
}

/** The same board silhouette, at portrait scale, for characters without a GLB. */
export function createPortraitStandIn(scene: Scene, character: string): Mesh {
  const mesh = createBody(scene, SHAPES[character] ?? 'pawn', 2);
  const material = new StandardMaterial('portrait-stand-in', scene);
  const element = elementOf(character);
  material.diffuseColor = Color3.FromHexString(element ? ELEMENT_COLORS[element] : '#c9b785');
  mesh.material = material;
  return mesh;
}

function createBody(scene: Scene, shape: Shape, s: number): Mesh {
  switch (shape) {
    case 'pawn': {
      const stem = MeshBuilder.CreateCylinder(
        'stem',
        { height: s * 0.32, diameterTop: s * 0.2, diameterBottom: s * 0.34, tessellation: 16 },
        scene,
      );
      stem.position.y = s * 0.26;
      const head = MeshBuilder.CreateSphere('head', { diameter: s * 0.28, segments: 12 }, scene);
      head.position.y = s * 0.54;
      return Mesh.MergeMeshes([stem, head], true, true, undefined, false, false)!;
    }
    case 'scout': {
      const cone = MeshBuilder.CreateCylinder(
        'cone',
        { height: s * 0.62, diameterTop: 0, diameterBottom: s * 0.4, tessellation: 16 },
        scene,
      );
      cone.position.y = s * 0.41;
      return cone;
    }
    case 'heavy': {
      const block = MeshBuilder.CreateBox(
        'block',
        { width: s * 0.42, height: s * 0.5, depth: s * 0.42 },
        scene,
      );
      block.position.y = s * 0.35;
      return block;
    }
    case 'runner': {
      const capsule = MeshBuilder.CreateCapsule(
        'capsule',
        { height: s * 0.7, radius: s * 0.15, tessellation: 12, subdivisions: 4 },
        scene,
      );
      capsule.position.y = s * 0.45;
      return capsule;
    }
    case 'flyer': {
      const ring = MeshBuilder.CreateTorus(
        'ring',
        { diameter: s * 0.42, thickness: s * 0.09, tessellation: 20 },
        scene,
      );
      ring.rotation.x = Math.PI / 2;
      ring.position.y = s * 0.42;
      const orb = MeshBuilder.CreateSphere('orb', { diameter: s * 0.18, segments: 10 }, scene);
      orb.position.y = s * 0.42;
      return Mesh.MergeMeshes([ring, orb], true, true, undefined, false, false)!;
    }
    case 'mage': {
      const robe = MeshBuilder.CreateCylinder(
        'robe',
        { height: s * 0.56, diameterTop: s * 0.12, diameterBottom: s * 0.4, tessellation: 16 },
        scene,
      );
      robe.position.y = s * 0.38;
      const orb = MeshBuilder.CreateSphere('orb', { diameter: s * 0.22, segments: 12 }, scene);
      orb.position.y = s * 0.78;
      return Mesh.MergeMeshes([robe, orb], true, true, undefined, false, false)!;
    }
  }
}
